import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseVaultPage } from '../../document-vault/hooks/vaultQueryParams';

/**
 * The Key Dates page keeps its view state (tab and page) in the URL query, e.g.
 * `/key-dates-documents?tab=subscriptions&page=2`.
 * Every tab / page change creates a history entry, so the browser Back button from a document
 * details page returns to exactly the same tab and page.
 */
export const KEY_DATES_QUERY_PARAMS = {
  TAB: 'tab',
  PAGE: 'page',
  /** Legacy param still used by the document details breadcrumb ("?activeTab=Subscriptions"). */
  LEGACY_ACTIVE_TAB: 'activeTab',
} as const;

export type KeyDatesTab = 'Documents' | 'Subscriptions';

export type KeyDatesQueryState = {
  tab: KeyDatesTab;
  page: number;
};

const SUBSCRIPTIONS_TAB_VALUE = 'subscriptions';

const parseTab = (value: string | null): KeyDatesTab =>
  value?.trim().toLowerCase() === SUBSCRIPTIONS_TAB_VALUE ? 'Subscriptions' : 'Documents';

export const readKeyDatesQuery = (params: URLSearchParams): KeyDatesQueryState => ({
  tab: parseTab(
    params.get(KEY_DATES_QUERY_PARAMS.TAB) ?? params.get(KEY_DATES_QUERY_PARAMS.LEGACY_ACTIVE_TAB)
  ),
  page: parseVaultPage(params.get(KEY_DATES_QUERY_PARAMS.PAGE)),
});

/**
 * Returns a copy of `params` with the Key Dates state written in its canonical form.
 * Defaults are omitted (Documents tab, page 1). Unrelated params are preserved.
 */
export const writeKeyDatesQuery = (
  params: URLSearchParams,
  state: KeyDatesQueryState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(KEY_DATES_QUERY_PARAMS.LEGACY_ACTIVE_TAB);

  if (state.tab === 'Subscriptions') {
    next.set(KEY_DATES_QUERY_PARAMS.TAB, SUBSCRIPTIONS_TAB_VALUE);
  } else {
    next.delete(KEY_DATES_QUERY_PARAMS.TAB);
  }

  if (state.page > 1) {
    next.set(KEY_DATES_QUERY_PARAMS.PAGE, String(state.page));
  } else {
    next.delete(KEY_DATES_QUERY_PARAMS.PAGE);
  }

  return next;
};

export type UpdateKeyDatesQuery = (
  changes: Partial<KeyDatesQueryState>,
  options?: { replace?: boolean }
) => void;

/**
 * Reads / writes the Key Dates view state from / to the URL and normalizes legacy / invalid
 * params in place (without a new history entry).
 */
export const useKeyDatesQuery = () => {
  const [params, setSearchParams] = useSearchParams();
  const { tab, page } = useMemo(() => readKeyDatesQuery(params), [params]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateKeyDatesQuery: UpdateKeyDatesQuery = useCallback(
    (changes, options) => {
      // Async callbacks (e.g. a finished request) must not change the URL of another page.
      if (!isMountedRef.current) {
        return;
      }
      const current = new URLSearchParams(window.location.search);
      const next = writeKeyDatesQuery(current, { ...readKeyDatesQuery(current), ...changes });
      if (next.toString() !== current.toString()) {
        setSearchParams(next, { replace: options?.replace ?? false });
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    const canonical = writeKeyDatesQuery(params, readKeyDatesQuery(params));
    if (canonical.toString() !== params.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [params, setSearchParams]);

  return { tab, page, updateKeyDatesQuery };
};
